export type LatinRequisiteValidationReason =
  | 'empty'
  | 'no_letters'
  | 'not_latin'
  | 'too_long';

export interface ValidateLatinRequisiteOptions {
  isRequired?: boolean;
  maxLength?: number;
}

export interface LatinRequisiteValidationResult {
  isValid: boolean;
  reason: LatinRequisiteValidationReason | null;
}

export type CjkRequisiteValidationReason =
  | 'empty'
  | 'no_cjk';

export interface ValidateCjkRequisiteOptions {
  isRequired?: boolean;
}

export interface CjkRequisiteValidationResult {
  isValid: boolean;
  reason: CjkRequisiteValidationReason | null;
}

export interface ForeignTradeBackendFieldError {
  expected?: string;
  field?: string;
  message?: string;
  reason?: string;
}

export const isAllowedLatinLetter = (codePoint: number): boolean => {
  return (
    (codePoint >= 0x0041 && codePoint <= 0x005A) ||
    (codePoint >= 0x0061 && codePoint <= 0x007A) ||
    (codePoint >= 0x00C0 && codePoint <= 0x024F) ||
    (codePoint >= 0x1E00 && codePoint <= 0x1EFF) ||
    (codePoint >= 0x2C60 && codePoint <= 0x2C7F) ||
    (codePoint >= 0xA720 && codePoint <= 0xA7FF) ||
    (codePoint >= 0xAB30 && codePoint <= 0xAB6F)
  );
};

export const isAllowedCjkIdeograph = (codePoint: number): boolean => {
  return (
    (codePoint >= 0x3400 && codePoint <= 0x4DBF) ||
    (codePoint >= 0x4E00 && codePoint <= 0x9FFF) ||
    (codePoint >= 0xF900 && codePoint <= 0xFAFF) ||
    (codePoint >= 0x20000 && codePoint <= 0x2FA1F)
  );
};

export const isBackendWhitespaceChar = (char: string): boolean => {
  const codePoint = char.codePointAt(0);

  if (codePoint === undefined) return false;

  if (codePoint >= 0x0009 && codePoint <= 0x000D) return true;
  if (codePoint === 0x0085) return true;

  return /^[\p{Zs}\p{Zl}\p{Zp}]$/u.test(char);
};

export const isStrictlyWhitespaceOrEmpty = (value: string | null | undefined): boolean => {
  if (value == null || value === '') return true;

  for (const char of value) {
    if (!isBackendWhitespaceChar(char)) {
      return false;
    }
  }

  return true;
};

export const trimBackendWhitespace = (value: string): string => {
  let startIndex = 0;
  let endIndex = value.length;

  while (startIndex < endIndex && isBackendWhitespaceChar(value[startIndex])) {
    startIndex += 1;
  }

  while (endIndex > startIndex && isBackendWhitespaceChar(value[endIndex - 1])) {
    endIndex -= 1;
  }

  return value.slice(startIndex, endIndex);
};

export const validateLatinRequisite = (
  value: string | null | undefined,
  options: ValidateLatinRequisiteOptions = {}
): LatinRequisiteValidationResult => {
  const {
    isRequired = true,
    maxLength
  } = options;

  if (isStrictlyWhitespaceOrEmpty(value)) {
    return {
      isValid: !isRequired,
      reason: isRequired ? 'empty' : null
    };
  }

  const trimmedValue = trimBackendWhitespace(value!);

  if (maxLength !== undefined && trimmedValue.length > maxLength) {
    return {
      isValid: false,
      reason: 'too_long'
    };
  }

  let hasAllowedBaseLetter = false;

  for (const char of trimmedValue) {
    const isLetter = /^\p{L}$/u.test(char);

    if (isLetter) {
      const codePoint = char.codePointAt(0)!;

      if (!isAllowedLatinLetter(codePoint)) {
        return {
          isValid: false,
          reason: 'not_latin'
        };
      }

      if (/^[\p{Lu}\p{Ll}\p{Lt}\p{Lo}]$/u.test(char)) {
        hasAllowedBaseLetter = true;
      }
    }
  }

  if (!hasAllowedBaseLetter) {
    return {
      isValid: false,
      reason: 'no_letters'
    };
  }

  return {
    isValid: true,
    reason: null
  };
};

export const validateCjkRequisite = (
  value: string | null | undefined,
  options: ValidateCjkRequisiteOptions = {}
): CjkRequisiteValidationResult => {
  const { isRequired = true } = options;

  if (isStrictlyWhitespaceOrEmpty(value)) {
    return {
      isValid: !isRequired,
      reason: isRequired ? 'empty' : null
    };
  }

  for (const char of value!) {
    const codePoint = char.codePointAt(0)!;

    if (isAllowedCjkIdeograph(codePoint)) {
      return {
        isValid: true,
        reason: null
      };
    }
  }

  return {
    isValid: false,
    reason: 'no_cjk'
  };
};

export const getForeignTradeBackendErrorText = (
  error: ForeignTradeBackendFieldError,
  fallbackMessage = 'Неверное значение'
): string => {
  if (error.message) return error.message;

  const expected = error.expected?.toLowerCase();
  const reason = error.reason?.toLowerCase();

  if (expected === 'latin' || reason?.includes('latin')) {
    return 'Используйте латиницу';
  }

  if (expected === 'chinese' || expected === 'cjk' || reason?.includes('chinese') || reason?.includes('cjk')) {
    return 'Укажите значение с китайскими иероглифами';
  }

  if (reason === 'required' || reason === 'empty') {
    return 'Заполните поле';
  }

  if (reason === 'no_letters') {
    return 'Укажите значение, содержащее буквы';
  }

  if (reason === 'too_long') {
    return 'Превышена допустимая длина';
  }

  return error.reason || fallbackMessage;
};